'use client'

import React, { ComponentType, useMemo, useState } from 'react'
import { useRouter } from 'lib/useRouter'
import { Group, Option, Select } from '@/components/ui-other/basic/NewSelect'

type FilterLink = {
  addFilterPath: string
  removeFilterPath: string
  text: string
  selected: boolean
  activeEqualsTo?: string[]
  activeEqualsToFunc?: () => boolean
}

type FilterGroup = {
  title: string
  links: FilterLink[]
}

type FilterProps = {
  title?: string
  links: Array<FilterLink | FilterGroup>
  removable: boolean
  rounded?: boolean
  className?: string
  optionStyle?: object
}

const filterItemToOption = (item: FilterLink | FilterGroup): Option | Group => {
  if ('links' in item) {
    return {
      label: item.title,
      options: item.links.map((link) => filterItemToOption(link) as Option)
    }
  } else {
    return {
      label: item.text,
      value: item
    }
  }
}

const findActiveFilterLink = (
  items: Array<FilterLink | FilterGroup>
): Option[] => {
  const selected = []

  for (const item of items) {
    if ('links' in item) {
      selected.push(...findActiveFilterLink(item.links))
    } else if (item.selected) {
      selected.push({
        label: item.text,
        value: item
      })
    }
  }
  return selected
}

const Filter: ComponentType<FilterProps> = ({
  title,
  links,
  removable,
  rounded = true,
  className,
  optionStyle
}) => {
  const options = links.map(filterItemToOption)
  const activeFilterLink = findActiveFilterLink(links)
  const [selectedOptions, setSelectedOptions] =
    useState<Option[]>(activeFilterLink)
  const { push } = useRouter()

  const handleOnChange = (option: Option | readonly Option[] | null) => {
    if (option && 'value' in option) {
      setSelectedOptions([option])
      push(option.value.addFilterPath)
    } else if (!option && links.length > 0) {
      setSelectedOptions([])
      const removeFilterPath = getRemoveFilterPath(links[0])
      if (removeFilterPath) {
        push(removeFilterPath)
      }
    }
  }

  const isDisabled = useMemo(() => !options || options.length === 0, [options])
  return (
    <div className={className || ''}>
      <Select
        ariaLabel={title}
        value={selectedOptions}
        isDisabled={isDisabled}
        rounded={rounded}
        placeholder={title}
        isClearable={removable}
        onChange={handleOnChange}
        options={options}
        optionStyle={optionStyle}
        // formatGroupLabel={(data) => (
        //   <div style={{ fontWeight: 'bold' }}>{data.label}</div>
        // )}
      />
    </div>
  )
}

function getRemoveFilterPath(item: FilterLink | FilterGroup) {
  if ('removeFilterPath' in item) {
    return item.removeFilterPath
  } else if (Array.isArray(item.links)) {
    return getRemoveFilterPath(item.links[0])
  }
  return null
}

export default Filter
